import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { Dialog, useAction, useRueckfrage } from "../../../components/ui";
import { date, time } from "../../../lib/format";
import { KitAnsicht } from "../../oeffentlich/KitAnsicht";
import { ohnePreise, type KitView } from "../../oeffentlich/kit-model";
import { useBetraegeSichtbar } from "../../../app/betraege";
import {
  checkKitInput, createKit, fehlendeEnglischeFelder, kitLink, KIT_STATE_LABEL, kitState, previewKit, revokeKit, sendKit, type KitCreateInput,
  type KitProfileRow, type KitWithMeta,
} from "./mediakit-daten";
import { CopyLink } from "./parts";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import { t } from "../../../lib/sprache";

/**
 * Links für Media-Kit und Pitch-Liste (0074): anlegen (befristet, mit Umfang), Liste mit Stand, Aufrufen und Protokoll,
 * Vorschau wie bei der Marke, Versand über den Postausgang, Zurückziehen. Genutzt im Media-Kit des Creators und auf der Seite
 * Pitch-Listen.
 * 0099 (Tarifstaffel): Anlegen und Versand erst mit „Media-Kits und Pitch-Listen“; bestehende Links bleiben gültig, Vorschau,
 * Protokoll und Zurückziehen gehen in jedem Tarif.
 */

export interface KitOptions {
  brands: { id: string; name: string }[];
  defaultLanguage: "de" | "en";
  /** K-539: aus einer Anfrage vorbelegt (Marke, Ansprechperson, Titel) */
  vorbelegt?: { brandId?: string | null; email?: string | null; title?: string | null };
}

export function LinkFormular({ orgId, talentIds, options, onCreated, extra }: {
  orgId: string; talentIds: string[]; options: KitOptions; onCreated?: (kitId: string) => void; extra?: React.ReactNode;
}) {
  const { busy, error, run } = useAction();
  const gesperrt = useModul("module_media_kit").gesperrt;
  const [f, setF] = useState<Omit<KitCreateInput, "talentIds">>({
    email: options.vorbelegt?.email ?? "", title: options.vorbelegt?.title ?? "", brandId: options.vorbelegt?.brandId ?? null, language: options.defaultLanguage, days: 30,
    showRates: false, showReferences: true, showInstagram: true, message: "",
  });
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState<{ token: string; kitId: string; language: "de" | "en" } | null>(null);
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  // Englischer Link: fehlende englische Felder je Creator nennen, mit Sprung ins Media-Kit (SICHT-AGENTUR-REST-16)
  const englisch = f.language === "en" && talentIds.length > 0;
  const luecken = useQuery({
    queryKey: ["kit-en-luecken", orgId, [...talentIds].sort().join(",")],
    enabled: englisch,
    staleTime: 30_000,
    queryFn: async () => {
      const [profile, talente] = await Promise.all([
        api.select<Pick<KitProfileRow, "talent_id" | "bio_de" | "bio_en" | "topics" | "topics_en" | "rates">>("talent_kit_profiles", {
          columns: "talent_id, bio_de, bio_en, topics, topics_en, rates", filters: [["talent_id", "in", talentIds]] }),
        api.select<{ id: string; display_name: string | null; first_name: string; last_name: string | null }>("talents", {
          columns: "id, display_name, first_name, last_name", filters: [["id", "in", talentIds]] }),
      ]);
      const name = (id: string) => {
        const x = talente.find((r) => r.id === id);
        return x ? (x.display_name?.trim() || [x.first_name, x.last_name].filter(Boolean).join(" ")) : "Creator";
      };
      return talentIds.map((id) => ({ id, name: name(id), felder: fehlendeEnglischeFelder(profile.find((p) => p.talent_id === id)) })).filter((x) => x.felder.length > 0);
    },
  });

  async function anlegen() {
    if (gesperrt) return;
    const input = { ...f, talentIds };
    const e = checkKitInput(input);
    setFehler(e);
    if (e) return;
    const r = await run("kit-neu", () => createKit(api, orgId, input), talentIds.length > 1 ? t("Pitch-Liste angelegt.", "Pitch list created.") : t("Link zum Media-Kit angelegt.", "Media kit link created."));
    if (r) {
      setNeu({ token: r.token, kitId: r.kit_id, language: f.language });
      onCreated?.(r.kit_id);
    }
  }

  return (
    <section className="panel stack" aria-labelledby="kit-link-neu">
      <h2 id="kit-link-neu">{talentIds.length > 1 ? t("Pitch-Liste als Link", "Pitch list as a link") : t("Media-Kit als Link", "Media kit as a link")}</h2>
      {extra}
      <div className="grid">
        <label>{t("E-Mail-Adresse der Marke", "Brand email address")}<input id="kit-mail" type="email" inputMode="email" value={f.email} onChange={(e) => set({ email: e.target.value })} placeholder="casting@marke.example" /></label>
        <label>
          {t("Marke (optional)", "Brand (optional)")}<select value={f.brandId ?? ""} onChange={(e) => set({ brandId: e.target.value || null })}>
            <option value="">{t("Ohne Marke", "Without brand")}</option>
            {options.brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label>{t("Titel (optional)", "Title (optional)")}<input maxLength={120} value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder={talentIds.length > 1 ? t("z. B. Casting Herbstkampagne", "e.g. autumn campaign casting") : ""} /></label>
        <label>
          {t("Sprache der Seite", "Page language")}<select value={f.language} onChange={(e) => set({ language: e.target.value as "de" | "en" })}>
            <option value="de">{t("Deutsch", "German")}</option>
            <option value="en">{t("Englisch", "English")}</option>
          </select>
        </label>
        <label>{t("Gültig für Tage", "Valid for days")}<input type="number" min="1" max="90" step="1" value={f.days} onChange={(e) => set({ days: Number(e.target.value) })} /></label>
      </div>
      {englisch && (luecken.data?.length ?? 0) > 0 && (
        <div className="notice stack tk-gap-sm" role="status">
          <p><strong>{t("Englische Fassung unvollständig.", "English version incomplete.")}</strong> {t("Wo ein englisches Feld leer ist, zeigt der Link der Marke die deutsche Fassung.", "Where an English field is empty, the brand's link shows the German version.")}</p>
          <ul>
            {luecken.data!.map((l) => (
              <li key={l.id}>{l.name}: {l.felder.join(", ")} · <Link to={`/agentur/creator/${l.id}/mediakit`}>{t("Im Media-Kit ergänzen", "Complete in the media kit")}</Link></li>
            ))}
          </ul>
        </div>
      )}
      <div className="stack tk-gap-sm">
        <label className="row"><input type="checkbox" checked={f.showInstagram} onChange={(e) => set({ showInstagram: e.target.checked })} />{t("Instagram-Werte zeigen (nur mit Einwilligung des Creators und nur freigegebene Blöcke)", "Show Instagram values (only with the creator's consent and only released blocks)")}</label>
        <label className="row"><input type="checkbox" checked={f.showReferences} onChange={(e) => set({ showReferences: e.target.checked })} />{t("Referenzen zeigen", "Show references")}</label>
        <label className="row"><input type="checkbox" checked={f.showRates} onChange={(e) => set({ showRates: e.target.checked })} />{t("Preise zeigen", "Show prices")}</label>
      </div>
      <label>{t("Nachricht an die Marke (optional)", "Message to the brand (optional)")}<textarea rows={3} maxLength={2000} value={f.message} aria-describedby="kit-nachricht-hinweis" onChange={(e) => set({ message: e.target.value })} />
        <small id="kit-nachricht-hinweis">{t("Die Nachricht geht erst mit, wenn du den Link unten mit „Per Mail senden“ verschickst. „Link erstellen“ allein schickt der Marke nichts.", "The message is only included when you send the link below with “Send by email”. “Create link” alone sends nothing to the brand.")}</small></label>
      {(fehler ?? error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
      {gesperrt && <TarifHinweis modul="module_media_kit" id="kit-neu-tarif" was={talentIds.length > 1 ? t("Neue Pitch-Listen", "New pitch lists") : t("Neue Media-Kit-Links", "New media kit links")} />}
      <div className="row">
        <button type="button" className="primary" disabled={!!busy || talentIds.length === 0 || gesperrt} aria-busy={busy === "kit-neu"} aria-describedby={gesperrt ? "kit-neu-tarif" : undefined} onClick={() => void anlegen()}>
          {busy === "kit-neu" ? t("Wird angelegt …", "Creating …") : t("Link erstellen (noch nicht senden)", "Create link (not sent yet)")}
        </button>
      </div>
      {neu && (
        <div className="notice stack" role="status">
          <p><strong>{t("Link angelegt.", "Link created.")}</strong> {t("Er erscheint nur jetzt; die Datenbank speichert nur einen Prüfwert. Die Marke öffnet ihn mit der Adresse ", "It only appears now; the database only stores a check value. The brand opens it with the address ")}{f.email.trim().toLowerCase()}.</p>
          <CopyLink label={t("Link für die Marke", "Link for the brand")} url={kitLink(window.location.origin, neu.token, neu.language)} />
        </div>
      )}
      <small>{t("Der Link ist persönlich und befristet; jeder Aufruf und jeder Fehlversuch steht im Protokoll. Nach fünf falschen Adressen ist er gesperrt. Der Creator sieht in seinem Portal, was geteilt wird.", "The link is personal and time-limited; every view and every failed attempt is logged. After five wrong addresses it is locked. The creator sees in their portal what is shared.")}</small>
    </section>
  );
}

export function LinkListe({ kits, talentName, brandName, onChanged, empty }: {
  kits: KitWithMeta[]; talentName: (id: string) => string; brandName: (id: string | null) => string | null; onChanged: () => void; empty: string;
}) {
  const { busy, error, run } = useAction();
  const sendenGesperrt = useModul("module_media_kit").gesperrt;
  const [alle, setAlle] = useState(false);
  const [vorschau, setVorschau] = useState<{ view: KitView; kit: KitWithMeta } | null>(null);
  const betraegeSichtbar = useBetraegeSichtbar();
  const rueckfrage = useRueckfrage();
  const [protokoll, setProtokoll] = useState<KitWithMeta | null>(null);
  const [gesendet, setGesendet] = useState<{ kitId: string; token: string; language: "de" | "en" } | null>(null);
  const now = new Date();
  const liste = kits.filter((k) => alle || kitState(k, now) === "active" || kitState(k, now) === "locked");

  return (
    <section className="panel stack" aria-labelledby="kit-links">
      {rueckfrage.element}
      <div className="row between">
        <h2 id="kit-links">{t("Links an Marken", "Links to brands")}</h2>
        <label className="row"><input type="checkbox" checked={alle} onChange={(e) => setAlle(e.target.checked)} />{t("Auch abgelaufene und zurückgezogene", "Also expired and withdrawn ones")}</label>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
      {sendenGesperrt && liste.some((k) => kitState(k, now) === "active") && (
        <TarifHinweis modul="module_media_kit" id="kit-senden-tarif" was={t("Links per Mail senden", "Sending links by email")} />
      )}
      {liste.length === 0 ? <p>{empty}</p> : (
        <div className="tablewrap tk-cardtable">
          <table>
            <caption className="sr-only">{t("Links zu Media-Kits und Pitch-Listen", "Links to media kits and pitch lists")}</caption>
            <thead><tr><th scope="col">{t("Für und Creator", "For and creators")}</th><th scope="col">{t("Stand", "Status")}</th><th scope="col">{t("Aufrufe", "Views")}</th><th scope="col"><span className="sr-only">{t("Aktionen", "Actions")}</span></th></tr></thead>
            <tbody>
              {liste.map((k) => {
                const st = kitState(k, now);
                const ok = k.views.filter((v) => v.ok);
                const fail = k.views.length - ok.length;
                return (
                  <tr key={k.id}>
                    <td data-label={t("Für und Creator", "For and creators")}>
                      <div>
                        <strong>{k.title ?? brandName(k.brand_id) ?? k.recipient_email}</strong>
                        <br /><small>{k.recipient_email}{k.brand_id && k.title ? ` · ${brandName(k.brand_id)}` : ""}</small>
                        <br /><small>{k.talentIds.map(talentName).join(", ")}</small>
                      </div>
                    </td>
                    <td data-label={t("Stand", "Status")}><div>
                      <span className={`pill ${st === "active" ? "success" : st === "locked" ? "danger" : ""}`}>{KIT_STATE_LABEL[st]}</span>
                      <br /><small>{t("bis ", "until ")}{date(k.expires_at)}{[k.show_instagram ? "Instagram" : null, k.show_references ? t("Referenzen", "References") : null, k.show_rates ? t("Preise", "Pricing") : null].filter(Boolean).length ? ` · ${[k.show_instagram ? "Instagram" : null, k.show_references ? t("Referenzen", "References") : null, k.show_rates ? t("Preise", "Pricing") : null].filter(Boolean).join(", ")}` : ""}</small>
                    </div></td>
                    <td data-label={t("Aufrufe", "Views")}><div>{ok.length}{fail ? ` · ${fail} ${fail === 1 ? t("Fehlversuch", "failed attempt") : t("Fehlversuche", "failed attempts")}` : ""}{ok[0] ? <><br /><small>{t("zuletzt ", "last ")}{date(ok[0].at)} {time(ok[0].at)}</small></> : null}</div></td>
                    <td>
                      <div className="row" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <button type="button" disabled={!!busy} onClick={() => void run(`vorschau-${k.id}`, async () => setVorschau({ view: await previewKit(api, k.id), kit: k }))}>{t("Vorschau", "Preview")}</button>
                        <button type="button" onClick={() => setProtokoll(k)}>{t("Protokoll", "Log")}</button>
                        {st === "active" && (
                          <button type="button" disabled={!!busy || sendenGesperrt} aria-busy={busy === `senden-${k.id}`} aria-describedby={sendenGesperrt ? "kit-senden-tarif" : undefined}
                            onClick={async () => {
                              // Praxistest K-034: erst fragen; der Versand macht einen schon kopierten Link ungültig
                              if (!(await rueckfrage.ask({ title: t("Media-Kit per Mail senden?", "Send media kit by email?"), text: t(`Die Mail geht an ${k.recipient_email}. Dabei entsteht ein neuer persönlicher Link; ein Link, den du vorher kopiert hast, gilt dann nicht mehr.`, `The email goes to ${k.recipient_email}. A new personal link is created; a link you copied before will no longer work.`), confirmLabel: t("Senden", "Send") }))) return;
                              void run(`senden-${k.id}`, async () => { const r = await sendKit(api, k.id, null); setGesendet({ kitId: k.id, token: r.token, language: k.language }); onChanged(); }, t("In den Postausgang gelegt. Der bisherige Link gilt nicht mehr.", "Placed in the outbox. The previous link no longer works."));
                            }}>
                            {t("Per Mail senden", "Send by email")}</button>
                        )}
                        {(st === "active" || st === "locked") && (
                          <button type="button" className="danger" disabled={!!busy} aria-busy={busy === `zurueck-${k.id}`}
                            onClick={async () => {
                              // Praxistest K-093: Zurückziehen ist endgültig, deshalb erst fragen
                              if (!(await rueckfrage.ask({ title: t("Link zurückziehen?", "Withdraw link?"), text: t(`Der Link an ${k.recipient_email} gilt dann sofort nicht mehr und lässt sich nicht wiederherstellen. Die Marke braucht einen neuen Link.`, `The link to ${k.recipient_email} stops working immediately and cannot be restored. The brand will need a new link.`), confirmLabel: t("Zurückziehen", "Withdraw"), danger: true }))) return;
                              void run(`zurueck-${k.id}`, async () => { await revokeKit(api, k.id); onChanged(); }, t("Link zurückgezogen.", "Link withdrawn."));
                            }}>
                            {t("Zurückziehen", "Withdraw")}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {gesendet && (
        <div className="notice stack" role="status">
          <p>{t("Die Mail an die Marke enthält einen neuen Link; der bisherige gilt nicht mehr.", "The email to the brand contains a new link; the previous one no longer works.")}</p>
          <CopyLink label={t("Neuer Link", "New link")} url={kitLink(window.location.origin, gesendet.token, gesendet.language)} />
        </div>
      )}
      <Dialog open={!!vorschau} onClose={() => setVorschau(null)} title={t("Vorschau: so sieht die Marke den Link", "Preview: this is how the brand sees the link")} wide>
        {vorschau && <KitAnsicht view={betraegeSichtbar ? vorschau.view : ohnePreise(vorschau.view)} embedded loadImage={ladeBildFuersTeam} />}
      </Dialog>
      <Dialog open={!!protokoll} onClose={() => setProtokoll(null)} title={t("Protokoll der Aufrufe", "View log")}>
        {protokoll && (protokoll.views.length === 0 ? <p>{t("Noch kein Aufruf.", "No views yet.")}</p> : (
          <ul>
            {protokoll.views.map((v, i) => <li key={i}>{date(v.at)} {time(v.at)} · {v.ok ? t("geöffnet", "opened") : t("falsche E-Mail-Adresse", "wrong email address")}</li>)}
          </ul>
        ))}
        <small>{t("Das Protokoll wird nach 180 Tagen gelöscht. Aufrufe des Teams (Vorschau, Teamansicht) stehen nicht darin.", "The log is deleted after 180 days. Team views (preview, team view) are not included.")}</small>
      </Dialog>
    </section>
  );
}

/** Bild in der Vorschau: aus dem Speicher der Agentur (das Team darf die Profilbilder seiner Creator lesen). */
export async function ladeBildFuersTeam(talentId: string, assetId?: string): Promise<Blob> {
  if (assetId) {
    // 0214: Foto der Collage oder Marken-Logo
    const a = await api.select<{ path: string }>("talent_kit_assets", { columns: "path", filters: [["id", "eq", assetId], ["talent_id", "eq", talentId]] });
    if (!a[0]?.path) throw new Error(t("kein Bild", "no image"));
    return api.download("documents", a[0].path);
  }
  const rows = await api.select<{ profile_image_path: string | null }>("talents", { columns: "profile_image_path", filters: [["id", "eq", talentId]] });
  if (!rows[0]?.profile_image_path) throw new Error(t("kein Bild", "no image"));
  return api.download("documents", rows[0].profile_image_path);
}
